import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AlertTriangle, CheckCircle2, Minus } from "lucide-react";

interface NoShowRiskProps {
  sentiment?: string | null;
  isNewPatient?: boolean;
  bookingIntentScore?: number | null;
  isEveningAppointment?: boolean;
}

export function calcNoShowRisk({
  sentiment,
  isNewPatient,
  bookingIntentScore,
  isEveningAppointment,
}: NoShowRiskProps): number {
  let risk = 0;
  if (sentiment === "frustrated") risk += 35;
  else if (sentiment === "confused") risk += 20;
  if (isNewPatient) risk += 15;
  if (isEveningAppointment) risk += 10;
  if (bookingIntentScore !== null && bookingIntentScore !== undefined) {
    if (bookingIntentScore < 50) risk += 20;
  }
  return Math.min(risk, 100);
}

interface NoShowRiskBadgeProps extends NoShowRiskProps {
  className?: string;
}

export const NoShowRiskBadge = ({
  sentiment,
  isNewPatient,
  bookingIntentScore,
  isEveningAppointment,
  className,
}: NoShowRiskBadgeProps) => {
  const risk = calcNoShowRisk({
    sentiment,
    isNewPatient,
    bookingIntentScore,
    isEveningAppointment,
  });

  const { bg, text, border, Icon, label } =
    risk >= 70
      ? {
          bg: "#FFF1F2",
          text: "#BE123C",
          border: "#FECDD3",
          Icon: AlertTriangle,
          label: "High risk",
        }
      : risk >= 40
        ? {
            bg: "#FFFBEB",
            text: "#B45309",
            border: "#FEF3C7",
            Icon: Minus,
            label: "Medium risk",
          }
        : {
            bg: "rgba(62,207,142,0.08)",
            text: "#1A8C5B",
            border: "rgba(62,207,142,0.2)",
            Icon: CheckCircle2,
            label: "Low risk",
          };

  const reasons: string[] = [];
  if (sentiment === "frustrated") reasons.push("caller sounded frustrated");
  if (sentiment === "confused") reasons.push("caller sounded confused");
  if (isNewPatient) reasons.push("new patient");
  if (isEveningAppointment) reasons.push("evening appointment");
  if (
    bookingIntentScore !== null &&
    bookingIntentScore !== undefined &&
    bookingIntentScore < 50
  )
    reasons.push("low booking intent score");
  const tooltip =
    risk === 0
      ? "No risk signals detected."
      : `Risk factors: ${reasons.join(", ")}.`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={
            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold cursor-default" +
            (className ? " " + className : "")
          }
          style={{ background: bg, color: text, borderColor: border }}
        >
          <Icon className="w-3 h-3" />
          {label} · {risk}%
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
};
